"use client";
import React, {
    useRef,
    useEffect,
    useState,
    useCallback,
    useMemo,
} from "react";
import styles from "../index.module.scss";
import { useProductsContext } from "@/src/provider";
import NewBTemplateTest from "../newBTemplateTest";
import ViewDetails from "../ViewDetails";
import { FmIcon } from "@/ui-component";
import { useTranslation } from "@/src/i18n/client";
import Box from "@mui/material/Box";
import { Card } from "@mui/material";
import { useFetchProducts } from "@/src/provider/Products/hooks";

// 扩展HTMLDivElement类型
interface CustomDivElement extends HTMLDivElement {
    startY?: number;
    isAtTop?: boolean;
}

// 节流函数
const throttle = (func: Function, limit: number) => {
    let inThrottle: boolean;
    let lastFunc: ReturnType<typeof setTimeout>;
    let lastTime: number;

    return function (this: any, ...args: any[]) {
        const context = this;
        if (!inThrottle) {
            func.apply(context, args);
            lastTime = Date.now();
            inThrottle = true;
        } else {
            clearTimeout(lastFunc);
            lastFunc = setTimeout(
                () => {
                    if (Date.now() - lastTime >= limit) {
                        func.apply(context, args);
                        lastTime = Date.now();
                    }
                },
                limit - (Date.now() - lastTime),
            );
        }
    };
};

// 计算可见范围
const calculateVisibleRange = (
    scrollTop: number,
    itemHeight: number,
    containerHeight: number,
    totalItems: number,
    visibleItems: number = 6,
    bufferItems: number = 2,
) => {
    const maxStartIndex = Math.max(0, totalItems - visibleItems);
    const startIndex = Math.min(
        maxStartIndex,
        Math.max(0, Math.floor(scrollTop / itemHeight) - bufferItems),
    );
    return {
        startIndex,
        endIndex: Math.min(startIndex + visibleItems + bufferItems, totalItems),
    };
};

const VerticalSwiper: React.FC<{
    PRE_SHOW_INDEX: number;
    secondProductRef: React.RefObject<HTMLDivElement>;
    setSticky: (sticky: boolean) => void;
}> = ({ PRE_SHOW_INDEX, secondProductRef, setSticky }) => {
    // 状态管理
    const productData = useProductsContext((state) => state.productData);
    const [products, setProducts] = useState(productData.data);
    const [visibleRange, setVisibleRange] = useState({
        startIndex: 0,
        endIndex:  6,
    });
    const [estimatedItemHeight, setEstimatedItemHeight] = useState(300);
    const [itemHeights, setItemHeights] = useState<{ [index: number]: number }>({});
    const hasTriggeredLoadMore = useRef(false);

    // Refs
    const ref = useRef<CustomDivElement>(null);
    const containerHeightRef = useRef(0);
    const { t } = useTranslation("common");
    const fetchProductsRes = useFetchProducts();

    // 数据监听
    useEffect(() => {
            setProducts(productData.data.slice(2));
    }, [productData]);

    // 初始高度测量
    useEffect(() => {
        const measureInitialHeight = () => {
            if (ref.current && ref.current.firstElementChild) {
                const firstItem = ref.current.firstElementChild as HTMLElement;
                const actualHeight = firstItem.offsetHeight;
                if (actualHeight > 0) {
                    setEstimatedItemHeight(actualHeight);
                }
            }
        };

        const timer = setTimeout(measureInitialHeight, 100);
        const handleResize = () => measureInitialHeight();
        window.addEventListener("resize", handleResize);

        return () => {
            clearTimeout(timer);
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    // 平滑更新估算高度
    const smoothUpdateEstimatedHeight = useCallback(
        (newHeight: number) => {
            const currentHeight = estimatedItemHeight;
            const heightDiff = newHeight - currentHeight;
            if (Math.abs(heightDiff) / currentHeight <= 0.1) {
                setEstimatedItemHeight(newHeight);
                return;
            }
            const step = heightDiff / 3;
            let currentStep = 0;
            const updateStep = () => {
                currentStep++;
                setEstimatedItemHeight((prev) => 
                    currentStep === 3 ? newHeight : prev + step
                );
                if (currentStep < 3) {
                    requestAnimationFrame(updateStep);
                }
            };
            updateStep();
        },
        [estimatedItemHeight],
    );

    // 测量项目高度
    const handleItemMeasure = useCallback(
        (index: number, height: number) => {
            setItemHeights((prev) => ({ ...prev, [index]: height }));
            const heights = Object.values({ ...itemHeights, [index]: height });
            const avgHeight = heights.length
                ? heights.reduce((sum, h) => sum + h, 0) / heights.length
                : estimatedItemHeight;
            smoothUpdateEstimatedHeight(avgHeight);
        },
        [estimatedItemHeight, itemHeights, smoothUpdateEstimatedHeight],
    );

    // 可见产品计算
    const visibleProducts = useMemo(() => {
        return products.slice(visibleRange.startIndex, visibleRange.endIndex);
    }, [products, visibleRange]);

    // 滚动到第二个产品
    const scrollToSecondProduct = useCallback(() => {
        const targetElement = secondProductRef.current;
        if (!targetElement) return;
        requestAnimationFrame(() => {
            if (!secondProductRef.current) return;
            const rect = secondProductRef.current.getBoundingClientRect();
            const targetTop = window.scrollY + rect.top - 50;
            window.scrollTo({ top: targetTop, behavior: "smooth" });
        });
    }, [secondProductRef]);

    // 滚动事件处理
    useEffect(() => {
        const scrollContainer = ref.current;
        if (!scrollContainer) return;

        containerHeightRef.current = scrollContainer.clientHeight;
        const throttledScroll = throttle((scrollTop: number) => {
            const totalItems = products.length;
            const containerHeight = containerHeightRef.current;
            const range = calculateVisibleRange(
                scrollTop,
                estimatedItemHeight,
                containerHeight,
                totalItems
            );
            if (range.startIndex !== visibleRange.startIndex || range.endIndex !== visibleRange.endIndex) {
                setVisibleRange(range);
            }
            const distanceToBottom = scrollContainer.scrollHeight - scrollTop - containerHeight;
        }, 30);

        const handleScroll = () => {
            if (ref.current) {
                throttledScroll(ref.current.scrollTop);
            }
        };

        scrollContainer.addEventListener("scroll", handleScroll, { passive: true });
        throttledScroll(scrollContainer.scrollTop);

        return () => scrollContainer.removeEventListener("scroll", handleScroll);
    }, [products.length, estimatedItemHeight, visibleRange]);

    // 触摸事件处理
    const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
        if (!ref.current) return;
        ref.current.startY = e.touches[0].clientY;
        ref.current.isAtTop = ref.current.scrollTop === 0;
    };

// 数据更新后重置标记
const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    e.stopPropagation()
    if (!ref.current) return;

    requestAnimationFrame(() => {
        const currentY = e.touches[0].clientY;
        const startY = ref.current!.startY || 0;
        const deltaY = currentY - startY;
        const scrollTop = ref.current!.scrollTop;

        // 顶部下拉逻辑
        if (ref.current!.isAtTop && deltaY > 0) {
            e.preventDefault();
            requestAnimationFrame(() => {
                scrollToSecondProduct();
                setSticky(false);
            });
            return; // 避免同时处理顶部下拉和上滑逻辑
        }

        // 计算倒数第三个是否真正进入可视区域
        const totalProducts = products.length;
        if (totalProducts < 3) return; // 至少需要3个产品

        const thirdLastIndex = totalProducts - 3;
        const { startIndex, endIndex } = visibleRange;

        // 严格判断：倒数第三个产品必须在可见范围内（去掉 bufferItems 的影响）
        const isThirdLastVisible = 
            thirdLastIndex >= startIndex && 
            thirdLastIndex < endIndex;

        // 获取倒数第三个产品的高度
        const thirdLastItemHeight = itemHeights[thirdLastIndex] || estimatedItemHeight;
        
        // 计算倒数第三个产品在视口中的位置（相对于容器顶部）
        const thirdLastItemTop = thirdLastIndex * estimatedItemHeight - scrollTop;
        const thirdLastItemBottom = thirdLastItemTop + thirdLastItemHeight;
        
        // 判断倒数第三个产品是否至少有一半进入视口
        const isThirdLastHalfVisible = 
            thirdLastItemTop < containerHeightRef.current / 2 && 
            thirdLastItemBottom > containerHeightRef.current / 2;

        // 仅在用户向上滑动且倒数第三个产品至少有一半进入视口时触发加载更多
        if (deltaY < 0 && isThirdLastVisible && isThirdLastHalfVisible && !hasTriggeredLoadMore.current) {
            console.log("加载更多（倒数第三个产品进入可视区域）");
            hasTriggeredLoadMore.current = true;
            fetchProductsRes("scroll",{ page: productData.pagination.current_page + 1 });
        }
        
        // 如果倒数第三个产品完全离开视口，重置标记
        if (thirdLastItemBottom < 0 || thirdLastItemTop > containerHeightRef.current) {
            hasTriggeredLoadMore.current = false;
        }
    });
};
useEffect(() => {
    hasTriggeredLoadMore.current = false;
}, [products]);

    // 清除状态组件
    const Clear = () => (
        <div
            className={styles["review-Photo-li"]}
            style={{ verticalAlign: "top", padding: "20px 0" }}
        >
            <FmIcon
                icon="icon--search-clear"
                className={styles["icon--search-clear"]}
            />
            <span>{t("common:NoMore")}</span>
        </div>
    );

const filterViews = (status: boolean) => {
    const filterDom = document.querySelector("#top-product");
    if (filterDom) {
        setSticky(false);
        const offset = 200; // 向上偏移 
        const targetPosition = filterDom.getBoundingClientRect().top + window.pageYOffset - offset;
        window.scrollTo({
            top: targetPosition,
            behavior: "smooth"
        });
    }
};

    return (
        <>
            <Box
                ref={ref}
                className={styles.contentContainer}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                style={{
                    overflowY: "auto",
                    scrollBehavior: "smooth",
                    width: "100vw",
                    height: "100dvh",
                }}
            >
                {/* 顶部占位元素 */}
                <div
                    style={{
                        height: `${visibleRange.startIndex * estimatedItemHeight}px`,
                        pointerEvents: "none",
                    }}
                    aria-hidden="true"
                />

                {/* 可见产品列表 */}
                {visibleProducts.map((item, index) => {
                    const actualIndex = visibleRange.startIndex + index;
                    const itemHeight = itemHeights[actualIndex] || estimatedItemHeight;
                    return (
                        <Card
                            key={`item-${actualIndex}`}
                            className={styles.carouselItem}
                            style={{
                                 minHeight: itemHeight,
                                // height:'100dvh',
                                opacity: 1,
                                transition: "opacity 0.2s ease, transform 0.1s ease",
                            }}
                            onLoad={(e) => {
                                if (e.currentTarget instanceof HTMLElement) {
                                    handleItemMeasure(actualIndex, e.currentTarget.offsetHeight);
                                }
                            }}
                        >
                            <NewBTemplateTest
                                data={item}
                                filterViews={filterViews}
                            />
                        </Card>
                    );
                })}

                {/* 底部占位元素 */}
                <div
                    style={{
                        height: `${Math.max(0, products.length - visibleRange.endIndex) * estimatedItemHeight}px`,
                        pointerEvents: "none",
                    }}
                    aria-hidden="true"
                />
                 {/* <Clear /> */}

                {/* 加载状态 */}
                {/* <FmLoading
                    visible={isLoading}
                    style={{
                        margin: "20px 0",
                        textAlign: "center",
                    }}
                /> */}
            </Box>

            <ViewDetails />
        </>
    );
};

export default React.memo(VerticalSwiper);
